import { create } from "zustand";
import { persist } from "zustand/middleware";

// crypto.randomUUID only exists in a secure context (HTTPS, or the special
// "localhost" exception) -- plain HTTP to an IP address or any other
// hostname is not one, so on a server reached that way this throws instead
// of returning an id. That crash used to happen inside push() itself,
// before the toast was ever added to state, so nothing rendered at all --
// not even the error this was trying to report. Falls back to a
// timestamp+random string, which is all a toast's id actually needs (unique
// enough to dedupe/dismiss by, nothing security-sensitive).

function generateId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    try {
      return crypto.randomUUID();
    } catch {
      // Fall through to the plain fallback below.
    }
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export const useUIStore = create(
  persist(
    (set) => ({
      sidebarCollapsed: false,
      mobileNavOpen: false,
      toggleSidebar: () => set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),
      setMobileNav: (open) => set({ mobileNavOpen: open }),
    }),
    { name: "hrms-ui", partialize: (s) => ({ sidebarCollapsed: s.sidebarCollapsed }) }
  )
);

// Toasts are ephemeral UI, deliberately not persisted.
export const useToastStore = create((set) => ({
  toasts: [],
  push: (toast) => {
    const id = generateId();
    set((s) => ({ toasts: [...s.toasts, { id, ...toast }] }));
    setTimeout(() => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })), toast.duration ?? 4500);
    return id;
  },
  dismiss: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
}));

export const toast = {
  success: (title, description) => useToastStore.getState().push({ variant: "success", title, description }),
  error: (title, description) => useToastStore.getState().push({ variant: "danger", title, description }),
  info: (title, description) => useToastStore.getState().push({ variant: "info", title, description }),
};
